// MoodEmojiPicker — simplified 6-emoji picker (W5 refactor: 30 → 6).
//
// Per AGENTS.md §3 #19: mood emoji ARE data, not chrome, so per-row
// emoji icons are allowed (the design-system ban only applies to UI chrome
// icons like FAB / ripple / snackbar; we use lucide-react everywhere else).
//
// Layout: 6×1 grid; each row shows one emoji + category label.
// Selecting writes the locked code with level fixed to 3 (middle intensity).
//
// Optional AI SUGGEST button surfaces the Laya-gated classify_mood
// endpoint when the sidecar is reachable; falls back silently if 503.

import { useState } from "react";
import { Loader2, Sparkles } from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils/cn";
import { ApiError } from "@/lib/api/pair";
import { classifyMood, type ClassifyMoodResponse } from "@/lib/api/diary";
import { diaryErrorMessage } from "@/lib/i18n/diary";
import { isLayaEnabled } from "@/lib/layaFlag";
import {
  EMOJI_FOR_CATEGORY,
  MOOD_CATEGORIES,
  MOOD_CATEGORY_LABEL_ZH,
  moodCode,
} from "@/lib/diary/mood";

interface MoodEmojiPickerProps {
  value: string | null;
  onChange: (next: string | null) => void;
  /** When provided, enables the AI suggest button + reads from content/title. */
  content?: string;
  title?: string;
  disabled?: boolean;
}

export function MoodEmojiPicker({
  value,
  onChange,
  content,
  title,
  disabled,
}: MoodEmojiPickerProps) {
  const [busy, setBusy] = useState(false);
  const [suggestError, setSuggestError] = useState<string | null>(null);
  const [recentSuggestion, setRecentSuggestion] = useState<ClassifyMoodResponse | null>(null);

  const handleSuggest = async () => {
    if (!content || content.trim().length < 1) {
      setSuggestError("請先輸入日記內容, 再試 AI 建議");
      return;
    }
    setBusy(true);
    setSuggestError(null);
    try {
      const result = await classifyMood({ content, title: title ?? null });
      setRecentSuggestion(result);
      onChange(result.mood);
    } catch (err) {
      if (err instanceof ApiError) {
        setSuggestError(diaryErrorMessage(err.code, err.message));
      } else {
        setSuggestError("AI 建議失敗, 請稍後再試");
      }
    } finally {
      setBusy(false);
    }
  };

  // Derive selected category from current mood code
  const selectedCat = value
    ? (value.split("_")[1] as (typeof MOOD_CATEGORIES)[number] | null)
    : null;

  return (
    <div className="flex flex-col gap-3" data-testid="mood-picker">
      <div className="flex items-center justify-between">
        <span className="font-label text-ink-2">今天的心情</span>
        {/*
          W49.0 §5. The AI suggest button is gated on `VITE_LAYA_ENABLED`, the
          flag `deploy/env.production.example` has declared and nothing read.
          The mood classifier behind it answers 503 while `LAYA_ENABLED=false`,
          so the button was guaranteed to fail. Absent beats present-and-broken:
          a visible control that cannot work costs a tap, a wait and an error
          message to teach something the build already knew.
        */}
        {content !== undefined && isLayaEnabled() ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={busy || disabled}
            onClick={handleSuggest}
            data-testid="mood-suggest"
            leadingIcon={
              busy ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Sparkles className="w-3.5 h-3.5" />
              )
            }
          >
            {busy ? "分析中…" : "AI 建議"}
          </Button>
        ) : null}
      </div>
      <div
        className="flex gap-2 overflow-x-auto -mx-4 px-4 pb-1"
        role="radiogroup"
        aria-label="心情分類"
      >
        {MOOD_CATEGORIES.map((cat) => {
          const code = moodCode(cat, 3); // fixed level 3 (middle)
          const selected = selectedCat === cat;
          const emoji = EMOJI_FOR_CATEGORY[cat];
          return (
            <button
              key={cat}
              type="button"
              role="radio"
              aria-checked={selected}
              aria-label={`${MOOD_CATEGORY_LABEL_ZH[cat]}`}
              title={MOOD_CATEGORY_LABEL_ZH[cat]}
              data-testid={`mood-${code}`}
              data-mood={code}
              disabled={disabled}
              onClick={() => onChange(selected ? null : code)}
              className={cn(
                "shrink-0 w-[72px] flex flex-col items-center gap-1 rounded-lg py-2.5",
                "transition-colors border press",
                selected
                  ? "bg-you border-you-strong"
                  : "bg-paper border-hairline hover:bg-paper-deep",
              )}
            >
              <span className="text-3xl leading-none" aria-hidden>
                {emoji}
              </span>
              <span className="text-body-xs text-ink-2">{MOOD_CATEGORY_LABEL_ZH[cat]}</span>
            </button>
          );
        })}
      </div>
      {value ? (
        <p className="text-body-sm text-ink-2" data-testid="mood-current" data-mood={value}>
          {EMOJI_FOR_CATEGORY[selectedCat!]} 已選擇
          {recentSuggestion?.mood === value && recentSuggestion ? (
            <span className="text-ink-3">
              {" "}
              · 信心 {(recentSuggestion.confidence * 100).toFixed(0)}%
            </span>
          ) : null}
        </p>
      ) : null}
      {suggestError ? (
        <p
          role="alert"
          className="text-body-sm text-partner-strong"
          data-testid="mood-suggest-error"
        >
          {suggestError}
        </p>
      ) : null}
    </div>
  );
}
